import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { Helmet } from 'react-helmet';

// Define reserved paths - removed all influencer-related paths
const RESERVED_PATHS = [
  'about', 'projects', 'pricing', 'contact', 'attendancesystem', 
  'services', 'digital-marketing', 'gorakhpur', 'delhi', 'patna',
  'lucknow', 'bihar', 'punjab', 'india', 'bangalore', 'hyderabad',
  'pune', 'chennai', 'gurgaon', 'himachal', 'faridabad', 'uk',
  'mohali', 'chandigarh', 'uttarpradesh', '404', 'blog', 'faq',
  'portfolio', 'solutions', 'enterprise', 'ecommerce', 'mobile-apps'
];

// Normalize path by removing trailing slashes and forcing lowercase
const normalizePath = (path) => {
  if (!path) return '/';
  return path.toLowerCase().replace(/\/$/, '') || '/';
};

// Enhanced route metadata with enterprise-focused SEO
const routes = {
  '/': {
    title: 'Vibhraad Technology - Software Development Company India | Custom IT Solutions',
    description: 'Vibhraad Technology is an enterprise software company based in Noida, India. Custom ERP, CRM, university portals, mobile apps & digital solutions. 57+ projects delivered across India, UK, Canada & Cambodia.',
    keywords: 'software development company india, custom software development, web development company, mobile app development india, IT solutions provider, enterprise software development, react js development company, node js development, python development services, digital transformation company india, best software company, software development services',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'TechnologyCompany',
      'name': 'Vibhraad Technology Pvt Ltd',
      'alternateName': 'Vibhraad Technology',
      'url': 'https://Vibhraad.com',
      'logo': 'https://i.ibb.co/N6HrptBN/Vibhraadlogo.png',
      'description': 'Leading software development company in India providing custom web development, mobile app development, and enterprise IT solutions.',
      'foundingDate': '2025',
      'numberOfEmployees': '10-50',
      'slogan': 'Enterprise Software Built Right',
      'areaServed': ['India', 'United Kingdom', 'Canada', 'Cambodia'],
      'serviceArea': {
        '@type': 'GeoCircle',
        'geoMidpoint': {
          '@type': 'GeoCoordinates',
          'latitude': 26.7606,
          'longitude': 83.3732
        },
        'geoRadius': '1000000'
      },
      'hasOfferCatalog': {
        '@type': 'OfferCatalog',
        'name': 'Software Development Services',
        'itemListElement': [
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Custom Software Development',
              'description': 'End-to-end custom software development services'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Web Application Development',
              'description': 'Modern web application development using latest technologies'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Mobile App Development',
              'description': 'Native and cross-platform mobile application development'
            }
          }
        ]
      },
      'contactPoint': {
        '@type': 'ContactPoint',
        'telephone': '+91',
        'contactType': 'customer service',
        'availableLanguage': ['English', 'Hindi']
      },
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': 'Gorakhpur',
        'addressRegion': 'Uttar Pradesh',
        'addressCountry': 'IN'
      },
      'sameAs': [
        'https://www.facebook.com/profile.php?id=61571805599409',
        'https://instagram.com/Vibhraad',
        'https://www.linkedin.com/company/Vibhraad/',
        'https://twitter.com/Vibhraad'
      ]
    }
  },
  '/about': {
    title: 'About Vibhraad Technology - Enterprise Software Company India',
    description: 'Vibhraad Technology Private Limited, incorporated October 2025 in Noida. CIN: U62091UP2025PTC234668. 57+ projects delivered across India, UK, Canada & Cambodia. Custom ERP, university portals, mobile apps.',
    keywords: 'about vibhraad technology, software development team india, experienced developers, IT company background, technology expertise, software development experience',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      'name': 'About Vibhraad Technology',
      'description': 'Learn about Vibhraad Technology, our expert team, mission, and commitment to delivering exceptional software solutions.'
    }
  },
  '/projects': {
    title: 'Software Development Portfolio - Vibhraad Technology Success Stories',
    description: 'Explore 500+ successful software projects by Vibhraad Technology. Enterprise applications, e-commerce platforms, mobile apps, and custom solutions across 20+ industries.',
    keywords: 'software development portfolio, web development projects, mobile app showcase, enterprise software examples, custom development case studies, IT project gallery',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      'name': 'Vibhraad Technology Project Portfolio',
      'description': 'Comprehensive portfolio showcasing successful software development projects and case studies.'
    }
  },
  '/pricing': {
    title: 'Software Development Pricing - Transparent Costs | Vibhraad Technology',
    description: 'Get transparent pricing for custom software development, web apps, mobile apps & IT solutions. Starting from ₹50,000. Free consultation & detailed project estimates available.',
    keywords: 'software development cost india, custom software pricing, web development rates, mobile app development cost, IT project pricing, software development budget, development cost estimation',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'PriceSpecification',
      'name': 'Software Development Pricing',
      'description': 'Transparent and competitive pricing for all software development services starting from ₹50,000.'
    }
  },
  '/contact': {
    title: 'Contact Vibhraad Technology - Free Software Development Consultation',
    description: 'Get free consultation for your software project. Contact Vibhraad Technology for custom development, web apps, mobile solutions. Expert advice & detailed project proposals.',
    keywords: 'contact software development company, free IT consultation, software development inquiry, custom development quote, technology consultation india',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      'name': 'Contact Vibhraad Technology',
      'description': 'Get in touch with Vibhraad Technology for expert software development consultation and project quotes.'
    }
  },
  '/digital-marketing': {
    title: 'Digital Marketing Services India - SEO, PPC, Social Media | Vibhraad',
    description: 'Comprehensive digital marketing services by Vibhraad Technology. Expert SEO, PPC campaigns, social media marketing, content strategy. Boost online presence & drive growth.',
    keywords: 'digital marketing services india, SEO company, PPC management, social media marketing, online marketing agency, digital marketing consultant, internet marketing services',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      'name': 'Digital Marketing Services',
      'provider': {
        '@type': 'Organization',
        'name': 'Vibhraad Technology'
      },
      'description': 'Complete digital marketing solutions including SEO, PPC, social media marketing, and content strategy.'
    }
  },
  '/services': {
    title: 'Software Development Services - Custom Solutions | Vibhraad Technology',
    description: 'Complete software development services: Custom software, web applications, mobile apps, enterprise solutions, cloud development, API integration. Expert team, proven methodology.',
    keywords: 'software development services, custom software development, web application development, mobile app development services, enterprise software solutions, cloud development services',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      'name': 'Software Development Services',
      'provider': {
        '@type': 'TechnologyCompany',
        'name': 'Vibhraad Technology'
      },
      'serviceType': 'Software Development',
      'description': 'Comprehensive software development services including custom applications, web development, mobile apps, and enterprise solutions.'
    }
  },
  '/attendancesystem': {
    title: 'Attendance Management System - Smart Employee Tracking | Vibhraad',
    description: 'Advanced attendance management system with biometric integration, real-time tracking, automated reports. Perfect for businesses of all sizes. Get demo today!',
    keywords: 'attendance management system, employee tracking software, biometric attendance system, workforce management, time tracking software, HR management system',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      'name': 'Vibhraad Attendance Management System',
      'applicationCategory': 'BusinessApplication',
      'description': 'Comprehensive attendance management system with advanced features for modern businesses.'
    }
  },
  // Enhanced location pages for better local SEO
  '/gorakhpur': {
    title: 'Software Development Company Gorakhpur - Custom IT Solutions | Vibhraad',
    description: 'Leading software development company in Gorakhpur. Expert web development, mobile apps, enterprise solutions. Local team, global standards. Free consultation available!',
    keywords: 'software development company gorakhpur, web development gorakhpur, mobile app development gorakhpur, IT company gorakhpur, custom software gorakhpur, technology solutions gorakhpur',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      'name': 'Vibhraad Technology - Gorakhpur',
      'description': 'Premier software development services in Gorakhpur offering custom solutions and IT expertise.',
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': 'Gorakhpur',
        'addressRegion': 'Uttar Pradesh',
        'addressCountry': 'IN'
      },
      'geo': {
        '@type': 'GeoCoordinates',
        'latitude': 26.7606,
        'longitude': 83.3732
      }
    }
  },
  '/delhi': {
    title: 'Software Development Company Delhi NCR - Enterprise Solutions | Vibhraad',
    description: 'Top software development company in Delhi NCR. Enterprise applications, scalable web solutions, mobile apps. Serving Fortune 500 companies. Get quote today!',
    keywords: 'software development company delhi, enterprise software development delhi, web development delhi ncr, mobile app development delhi, IT solutions delhi, custom software delhi',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      'name': 'Vibhraad Technology - Delhi NCR',
      'description': 'Enterprise-grade software development services in Delhi NCR with focus on scalable solutions.',
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': 'Delhi',
        'addressRegion': 'Delhi',
        'addressCountry': 'IN'
      }
    }
  },
  // Add more strategic service pages
  '/enterprise-solutions': {
    title: 'Enterprise Software Development - Scalable Business Solutions | Vibhraad',
    description: 'Custom enterprise software development for large organizations. ERP systems, CRM solutions, workflow automation, cloud migration. Proven track record with enterprises.',
    keywords: 'enterprise software development, ERP development, CRM solutions, business automation, enterprise applications, large scale software development',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      'name': 'Enterprise Software Development',
      'provider': {
        '@type': 'TechnologyCompany',
        'name': 'Vibhraad Technology'
      }
    }
  },
  '/ecommerce-development': {
    title: 'E-commerce Development Services - Online Store Solutions | Vibhraad',
    description: 'Custom e-commerce development services. Multi-vendor platforms, payment integration, mobile commerce, inventory management. Build your online business today!',
    keywords: 'ecommerce development, online store development, shopping cart development, multi vendor marketplace, payment gateway integration, mobile commerce',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      'name': 'E-commerce Development',
      'provider': {
        '@type': 'TechnologyCompany',
        'name': 'Vibhraad Technology'
      }
    }
  },
  '/mobile-app-development': {
    title: 'Mobile App Development Company India - iOS & Android Apps | Vibhraad',
    description: 'Professional mobile app development services. Native iOS, Android, React Native, Flutter apps. 200+ apps delivered. User-friendly designs, robust functionality.',
    keywords: 'mobile app development company, ios app development, android app development, react native development, flutter development, cross platform apps',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      'name': 'Mobile App Development',
      'provider': {
        '@type': 'TechnologyCompany',
        'name': 'Vibhraad Technology'
      }
    }
  },
  '/careers': {
    title: 'Careers at Vibhraad Technology - Join Our Engineering Team',
    description: 'Build your career at Vibhraad Technology. We are hiring software engineers, designers, and digital marketers. Remote-friendly, growth-focused culture. Apply now.',
    keywords: 'software engineering jobs india, tech careers, developer jobs noida, IT company hiring, software developer vacancy, digital marketing jobs',
    schema: { '@context': 'https://schema.org', '@type': 'JobPosting', 'hiringOrganization': { '@type': 'Organization', 'name': 'Vibhraad Technology Private Limited' } }
  },
  '/leadership': {
    title: 'Leadership Team - Vibhraad Technology | Founders & Directors',
    description: 'Meet the founders and directors of Vibhraad Technology — Kundan Kumar & Jeetendra Yadav. Learn about our leadership vision, company background, and values.',
    keywords: 'vibhraad technology founders, leadership team, directors vibhraad, Kundan Kumar, Jeetendra Yadav, software company leadership india',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      'name': 'Leadership - Vibhraad Technology',
      'description': 'Meet the co-founders and directors of Vibhraad Technology Private Limited.',
      'mainEntity': {
        '@type': 'Organization',
        'name': 'Vibhraad Technology Private Limited',
        'foundingDate': '2025',
        'founder': [
          { '@type': 'Person', 'name': 'Kundan Kumar', 'jobTitle': 'Co-Founder & Director' },
          { '@type': 'Person', 'name': 'Jeetendra Yadav', 'jobTitle': 'Co-Founder & Director' }
        ]
      }
    }
  },
  '/privacy': {
    title: 'Privacy Policy | Vibhraad Technology',
    description: 'Read the Vibhraad Technology Privacy Policy. We are committed to protecting your personal data and being transparent about how we collect, use, and safeguard your information.',
    keywords: 'vibhraad privacy policy, data protection, personal information policy, GDPR compliance, data handling policy',
    schema: { '@context': 'https://schema.org', '@type': 'WebPage', 'name': 'Privacy Policy - Vibhraad Technology', 'description': 'Privacy policy covering data collection, usage, and protection practices.' }
  },
  '/terms': {
    title: 'Terms of Service | Vibhraad Technology',
    description: 'Read the Vibhraad Technology Terms of Service. Understand our service delivery terms, payment terms, IP ownership, and legal agreements before engaging our services.',
    keywords: 'vibhraad terms of service, service agreement, software development contract terms, legal terms, payment terms',
    schema: { '@context': 'https://schema.org', '@type': 'WebPage', 'name': 'Terms of Service - Vibhraad Technology', 'description': 'Service terms and conditions governing engagements with Vibhraad Technology.' }
  },
  '/cookies': {
    title: 'Cookie Policy | Vibhraad Technology',
    description: 'Learn how Vibhraad Technology uses cookies on its website. We use essential, analytics, and preference cookies to improve your experience.',
    keywords: 'vibhraad cookie policy, cookies used, google analytics cookies, cookie consent, website tracking policy',
    schema: { '@context': 'https://schema.org', '@type': 'WebPage', 'name': 'Cookie Policy - Vibhraad Technology', 'description': 'Cookie usage policy for the Vibhraad Technology website.' }
  },
  '/404': {
    title: 'Page Not Found | Vibhraad Technology',
    description: 'The page you are looking for does not exist. Explore our software development services, portfolio, and solutions.',
    keywords: 'page not found, 404, error page',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      'name': 'Page Not Found',
      'description': 'Error page - the requested content could not be found.'
    }
  }
};

// Create a mapping for case-insensitive route matching
const normalizedRoutes = Object.keys(routes).reduce((acc, key) => {
  acc[normalizePath(key)] = routes[key];
  return acc;
}, {});

const SEOHandler = () => {
  const location = useLocation();
  const currentPath = normalizePath(location.pathname);
  
  // Determine page metadata based on current path
  const getPageMetadata = () => {
    // First check if we have an exact match in our defined routes
    let metadata = normalizedRoutes[currentPath];
    
    // Case-insensitive matching for alternate capitalizations
    if (!metadata) {
      const possibleKey = Object.keys(normalizedRoutes).find(
        key => key.toLowerCase() === currentPath.toLowerCase()
      );
      
      if (possibleKey) {
        metadata = normalizedRoutes[possibleKey];
      }
    }
    
    // Check for location-based routes if still no match
    if (!metadata) {
      const locationMatch = /^\/(gorakhpur|delhi|patna|lucknow|bihar|punjab|himachal|india|bangalore|hyderabad|pune|chennai|gurgaon)(\/.*)?$/i.exec(currentPath);
      
      if (locationMatch) {
        const location = locationMatch[1];
        const normalizedLocation = location.toLowerCase();
        metadata = normalizedRoutes[`/${normalizedLocation}`];
      }
    }
    
    // If still no match, use homepage as fallback
    if (!metadata) {
      metadata = normalizedRoutes['/'];
    }
    
    return metadata;
  };
  
  const pageMetadata = getPageMetadata();

  useEffect(() => {
    // Server-side custom 404 handling
    if (currentPath === '/404') {
      const metaElement = document.createElement('meta');
      metaElement.setAttribute('http-equiv', 'Status');
      metaElement.setAttribute('content', '404 Not Found');
      document.head.appendChild(metaElement);
    }
    
    // Update canonical link
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.href = `https://Vibhraad.com${currentPath}`;
    }

    // Track page view for analytics
    if (window.gtag) {
      window.gtag('config', 'G-EFHBKG0Q1C', {
        'page_path': currentPath
      });
    }
  }, [currentPath]);

  // Generate breadcrumb path segments
  const generateBreadcrumbItems = (path) => {
    if (path === '/') return [{
      '@type': 'ListItem',
      'position': 1,
      'name': 'Home',
      'item': 'https://Vibhraad.com'
    }];
    
    const pathSegments = path.split('/').filter(segment => segment);
    
    const breadcrumbItems = [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'Home',
        'item': 'https://Vibhraad.com'
      }
    ];

    let currentBreadcrumbPath = '';
    pathSegments.forEach((segment, index) => {
      currentBreadcrumbPath += `/${segment}`;
      breadcrumbItems.push({
        '@type': 'ListItem',
        'position': index + 2,
        'name': segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
        'item': `https://Vibhraad.com${currentBreadcrumbPath}`
      });
    });

    return breadcrumbItems;
  };

  // Generate breadcrumb schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': generateBreadcrumbItems(currentPath)
  };

  // Additional structured data for software company
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'Vibhraad Technology Pvt Ltd',
    'url': 'https://Vibhraad.com',
    'logo': 'https://i.ibb.co/N6HrptBN/Vibhraadlogo.png',
    'description': 'Leading software development company in India',
    'foundingDate': '2025',
    'founders': [
      { '@type': 'Person', 'name': 'Kundan Kumar' },
      { '@type': 'Person', 'name': 'Jeetendra Yadav' }
    ],
    'numberOfEmployees': '10-50',
    'industry': 'Software Development',
    'makesOffer': [
      {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Custom Software Development'
        }
      },
      {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Web Application Development'
        }
      },
      {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Mobile App Development'
        }
      }
    ]
  };

  return (
    <Helmet>
      <title>{pageMetadata.title}</title>
      <meta name="description" content={pageMetadata.description} />
      <meta name="keywords" content={pageMetadata.keywords} />
      
      {/* Enhanced meta tags for better SEO */}
      <meta name="author" content="Vibhraad Technology" />
      <meta name="publisher" content="Vibhraad Technology Pvt Ltd" />
      <meta name="copyright" content="Vibhraad Technology Pvt Ltd" />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      
      {/* Business specific meta tags */}
      <meta name="geo.region" content="IN" />
      <meta name="geo.placename" content="India" />
      <meta name="ICBM" content="26.7606, 83.3732" />
      <meta name="DC.title" content={pageMetadata.title} />
      
      {/* Status code for 404 pages */}
      {currentPath === '/404' && (
        <meta httpEquiv="Status" content="404 Not Found" />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Vibhraad Technology" />
      <meta property="og:url" content={`https://Vibhraad.com${currentPath}`} />
      <meta property="og:title" content={pageMetadata.title} />
      <meta property="og:description" content={pageMetadata.description} />
      <meta property="og:image" content="https://i.ibb.co/N6HrptBN/Vibhraadlogo.png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@VibhraadTech" />
      <meta name="twitter:creator" content="@VibhraadTech" />
      <meta name="twitter:url" content={`https://Vibhraad.com${currentPath}`} />
      <meta name="twitter:title" content={pageMetadata.title} />
      <meta name="twitter:description" content={pageMetadata.description} />
      <meta name="twitter:image" content="https://i.ibb.co/N6HrptBN/Vibhraadlogo.png" />

      {/* Canonical - critical for SEO */}
      <link rel="canonical" href={`https://Vibhraad.com${currentPath}`} id="canonical-link" />
      
      {/* Additional SEO links */}
      <link rel="publisher" href="https://www.facebook.com/profile.php?id=61571805599409" />
      <link rel="author" href="https://Vibhraad.com/about" />

      {/* Page-specific structured data */}
      <script type="application/ld+json">
        {JSON.stringify(pageMetadata.schema)}
      </script>

      {/* Organization schema for homepage */}
      {currentPath === '/' && (
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      )}

      {/* Breadcrumb schema for all non-homepage pages */}
      {currentPath !== '/' && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}

      {/* FAQ Schema for relevant pages */}
      {(currentPath === '/pricing' || currentPath === '/services') && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            'mainEntity': [
              {
                '@type': 'Question',
                'name': 'What software development services does Vibhraad Technology offer?',
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text': 'We offer custom software development, web applications, mobile apps, enterprise solutions, e-commerce platforms, and digital marketing services.'
                }
              },
              {
                '@type': 'Question',
                'name': 'How much does custom software development cost?',
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text': 'Our software development projects start from ₹50,000 and vary based on complexity, features, and timeline. Contact us for a detailed quote.'
                }
              }
            ]
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHandler;